import { action, computed, makeObservable, observable } from "mobx"
import { FormInstance } from "antd"

import { balancesStore } from "@src/app/balanceStore"
import {
  Currency,
  GlobalCurrenciesCollection,
} from "@src/app/CurrenciesCollection"
import {
  ConvertPairsItemType,
  GlobalConvertPairsCollection,
} from "@src/pages/convert/classes/ConvertPairsCollection"
import { catchErrors, convertsApi } from "@src/shared/api"
import {
  ConvertResponse,
  ConvertTokenResponse,
  NetworkAll,
  SymbolAll,
} from "@src/shared/api/generated"
import { loading } from "@src/shared/classes/utils/loading"

export type ConvertProcessState = {
  amount: string
  currencyFrom: string
  currencyTo: string
}

export type ConvertCurrency = Currency & {
  balance: any
}

const defaultState = () => ({ amount: "", currencyFrom: "", currencyTo: "" })

export class ConvertProcess extends loading(["fetchRate", "convert"]) {
  _balances = balancesStore
  _currencies = GlobalCurrenciesCollection()

  private abortController = new AbortController()

  convertData: ConvertResponse | null = null

  rateError: null | string = null

  rates: ConvertTokenResponse | null = null

  state: ConvertProcessState = defaultState()

  constructor(
    private form: FormInstance,
    public mode: "from" | "to" = "from",
    public pairs = GlobalConvertPairsCollection(),
  ) {
    super()

    makeObservable(this, {
      clear: action.bound,
      convert: action.bound,
      convertData: observable.shallow,
      currenciesFrom: computed,
      currenciesTo: computed,
      currencyFrom: computed,
      currencyLoading: computed,
      currencyTo: computed,
      fetchRate: action.bound,
      rateError: observable,
      rates: observable.shallow,
      resetState: action.bound,
      selectMaxBalanceCurrency: action.bound,
      setAmount: action.bound,
      setCurrencyFrom: action.bound,
      setCurrencyTo: action.bound,
      setMax: action.bound,
      state: observable.shallow,
    })
  }

  private _getSignal() {
    this.abortController = new AbortController()

    return this.abortController.signal
  }

  private currencyByPair(pairs: ConvertPairsItemType[]) {
    return pairs.reduce((acc: ConvertCurrency[], p) => {
      const currency = this.currencies.find(
        (c) => `${c.symbol}_${c.network}` === p.id,
      )

      if (currency) {
        acc.push(currency)
      }

      return acc
    }, [])
  }

  abortRequest() {
    if (this.abortController) {
      this.abortController.abort()
    }
  }

  clear() {
    this.convertData = null
    this.resetState()
  }

  async convert() {
    return convertsApi
      .scheduleToConvert({
        token: this.rates?.token,
      })
      .then(({ data: { data } }) => {
        if (data) {
          this.convertData = data
        }

        return data
      })
  }

  async fetchRate() {
    if (!this.currencyFrom || !this.currencyTo || !this.state.amount) {
      return
    }

    this.abortRequest()
    this.rateError = null

    try {
      const { data: { data } = {} } = await convertsApi.getConvertToken(
        this.currencyFrom.network as NetworkAll,
        this.currencyFrom.symbol as SymbolAll,
        this.currencyTo.network as NetworkAll,
        this.currencyTo.symbol as SymbolAll,
        this.state.amount,
        { signal: this._getSignal() },
      )

      if (data) {
        this.rates = {
          ...data,
        }

        return
      }

      this.rates = null
      this.rateError = "Unable to get an exchange rate. Please try again."
    } catch (e: any) {
      if (e?.code === "ERR_CANCELED" || e?.name === "CanceledError") return

      this.rates = null
      this.rateError =
        e?.response?.data?.error?.message ??
        "Unable to get an exchange rate. Please try again."

      catchErrors(e)
    }
  }

  resetState() {
    const preservedCurrency = this.isFromMode
      ? { currencyFrom: this.state.currencyFrom }
      : { currencyTo: this.state.currencyTo }

    this.state = {
      ...defaultState(),
      ...preservedCurrency,
    }

    this.rates = null
    this.rateError = null
    this.form.resetFields([
      "amount",
      this.isFromMode ? "currencyTo" : "currencyFrom",
    ])
  }

  selectMaxBalanceCurrency() {
    const validCurrencies = this.currenciesFrom.filter(
      (c) => c.balance && typeof c.balance?.balances?.available,
    )

    if (validCurrencies.length === 0) return null

    const max = validCurrencies.reduce((max, curr) =>
      Number(curr.balance?.balances?.available) >
      Number(max.balance?.balances?.available)
        ? curr
        : max,
    )

    this.setCurrencyFrom(max.id)

    return max
  }

  setAmount(value: string) {
    this.state.amount = value
    this.rates = null
    this.rateError = null
  }

  setCurrencyFrom(value: string) {
    const currency = this.currencies.find((item) => item.id === value)

    if (!currency || !this.pairs.hasFromPair(currency)) return

    this.state.currencyFrom = value
    this.rates = null
    this.rateError = null

    this.form.setFieldValue("currencyFrom", value)
  }

  setCurrencyTo(value: string) {
    this.state.currencyTo = value
    this.rates = null
    this.rateError = null

    this.form.setFieldValue("currencyTo", value)
  }

  setMax(f: FormInstance) {
    if (this.currencyFrom) {
      const a = this.currencyFrom.balance?.balances?.available
        ? String(this.currencyFrom.balance?.balances?.available)
        : ""

      this.setAmount(a)

      this.form.setFieldValue("amount", a)
      this.form.setFieldValue("currencyTo", this.currencyTo?.id)
      this.form.setFieldValue("currencyFrom", this.currencyFrom.id)
    }
  }

  get currencies(): ConvertCurrency[] {
    return this._currencies.items
      .map((i) => {
        const balance = this._balances.balances.find(
          (b) => `${b.symbol}_${b.network}` === `${i.symbol}_${i.network}`,
        )

        return {
          ...i,
          balance,
        }
      })
      .slice()
      .sort((a, b) => {
        if (a.network === "fiat") {
          return -1
        }

        return 0
      })
  }

  get currenciesFrom(): ConvertCurrency[] {
    const to = this.currencyTo

    return this.currencyByPair(
      to ? this.pairs.getFromPairsByTo(to) : this.pairs.from,
    )
  }

  get currenciesTo(): ConvertCurrency[] {
    const from = this.currencyFrom

    return this.currencyByPair(
      from ? this.pairs.getToPairsByFrom(from) : this.pairs.to,
    )
  }

  get currencyFrom() {
    const { currencyFrom: currencyFromValue } = this.state

    if (currencyFromValue) {
      return this.currencies.find((c) => c.id === currencyFromValue)
    }

    return null
  }

  get currencyLoading() {
    return this._currencies.isLoading || this.pairs.isLoading
  }

  get currencyTo() {
    const { currencyTo: currencyToValue } = this.state

    if (currencyToValue) {
      return this.currencies.find((c) => c.id === currencyToValue)
    }

    return null
  }

  get isFromMode() {
    return this.mode === "from"
  }
}
